import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {AppThunk, RootState} from "../../app/store";
import {ApplicationModel,
        ApplicationJSON,
        ApplicationFactory,
        ApplicationPageModel,
        ApplicationPageJSON,
        ApplicationPageFactory,
        TabModel,
        TabFactory} from "../../models/models";
import Config from "../../config";

interface ApplicationState {
  isFetching: boolean;
  application: ApplicationModel | null;
  applicationPages: ApplicationPageModel[];
  availableApplications: ApplicationModel[];
  tabs: TabModel[];
  error: string | null;
}

const initialState: ApplicationState = {
  isFetching: false,
  application: null,
  applicationPages: [],
  availableApplications: [],
  tabs: [],
  error: null
};

export const applicationSlice = createSlice({
  name: "application",
  initialState,
  reducers: {
    storeFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    storeApplication: (state, action: PayloadAction<ApplicationModel | null>) => {
      state.application = action.payload;
    },
    storeApplicationPages: (state, action: PayloadAction<ApplicationPageModel[]>) => {
      state.applicationPages = action.payload.map((thisPage) => thisPage);
    },
    storeAvailableApplications: (state, action: PayloadAction<ApplicationModel[]>) => {
      state.availableApplications = action.payload.map((application) => application);
    },
    storeTabs: (state, action: PayloadAction<TabModel[]>) => {
      state.tabs = action.payload.map((tab) => tab);
    },
    storeError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    purgeStore: (state) => {
      state.application = null;
      state.applicationPages = [];
      state.availableApplications = [];
      state.tabs = [];
      state.isFetching = false;
      state.error = null;
    },
  },
});



export const { storeApplication, storeApplicationPages, storeAvailableApplications, storeTabs, storeFetching, storeError, purgeStore } = applicationSlice.actions;


export const retrieveApplication =
  (appApiName: string | null,
   accessToken: string,
   handleInvalidToken: () => void): AppThunk =>
  async (dispatch) => {

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/r_app`;

    const headers = new Headers();
    const requestBody = {"app_api_name": appApiName};


    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");


    try {

      dispatch(purgeStore());
      dispatch(storeFetching(true));

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if(response.status===401){
        handleInvalidToken();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        if(!errorData.message){
          dispatch(storeError(response.statusText));
        } else{
          dispatch(storeError(errorData.message));
        }
        dispatch(storeFetching(false));
        return;
      } else {
        dispatch(storeError(null));
      }

      const data = await response.json();

      if (!data ||
          !data.selected_application ||
          !data.selected_application.application ||
          !data.selected_application.application_pages ||
          !data.selected_application.tab_relations ||
          !Array.isArray(data.selected_application.application_pages) ||
          !Array.isArray(data.selected_application.tab_relations)) {
          dispatch(storeError("Error when fetching Application"));
          dispatch(storeFetching(false));
          return;
      }

      if(!data.all_applications ||
         !Array.isArray(data.all_applications)){
          dispatch(storeError("Error when fetching Application"));
          dispatch(storeFetching(false));
          return;
         }

      const applicationFactory = new ApplicationFactory();

      dispatch(
        storeApplication(applicationFactory.fromJSON(data.selected_application.application))
      )

      dispatch(
          storeApplicationPages(data.selected_application.application_pages.map((thisJSON: ApplicationPageJSON) => {
             return new ApplicationPageFactory().fromJSON(thisJSON);
            }
          )
        )
      );

      dispatch(
          storeTabs(data.selected_application.tab_relations.map((tabRelationJson: any) => {
             const referencedTabJson = tabRelationJson.ReferencedTab_sys;
             return new TabFactory().fromJSON(referencedTabJson);
            }
          )
        )
      );

      dispatch(
        storeAvailableApplications(data.all_applications.map((thisApplication: ApplicationJSON) => applicationFactory.fromJSON(thisApplication)))
      );

      dispatch(storeFetching(false));

    } catch (error) {
      const errorMessage = "Unhandled error occurred";
      dispatch(storeError(errorMessage));
      dispatch(storeFetching(false));
      console.error(error);
    }
  };

export const selectIsFetching = (state: RootState) =>
  state.applicationStore.isFetching;

export const selectError = (state: RootState) =>
  state.applicationStore.error;

export const selectApplication = (state: RootState) =>{
    return state.applicationStore.application;
}

export const selectTabs = (state: RootState) =>{
    return state.applicationStore.tabs;
}

export const selectApplicationPages = (state: RootState) =>{
    return state.applicationStore.applicationPages;
}

export const selectAvailableApplications = (state: RootState) =>{
    return state.applicationStore.availableApplications;
}


export default applicationSlice.reducer;
